import { emit, getState } from "@/neon/events";

const selectors = {
  activeItem: "nav__item--active",
  activeMenu: "nav__menu--active",
  navTrigger: "[data-navmenu-trigger]",
  subMenu: "[data-nav-submenu]",
  headerNav: ".header__nav",
  navLink: ".nav__link",
  linkParent: ".nav__link-parent",
  itemParent: ".nav__item-parent",
  navDepthOne: ".nav--depth-1",
  navDepthTwo: ".nav--depth-2",
  navDepthThree: ".nav--depth-3",
  quickSearchTrigger: ".quick-search__trigger",
  headerAcccountIcon: ".header__icon--account",
  logoTrigger: ".header__logo-image",
};
import { animateMegaNavigation } from "@/lib/animation";

const navigation = node => {
  const header = node.querySelector(".header");

  let resizeWatcher = null;

  const headerNav = node.querySelector(selectors.headerNav);
  const navDepthOne = headerNav.querySelector(selectors.navDepthOne);
  const navDepthThree = headerNav.querySelectorAll(selectors.navDepthThree);
  const headerLinks = navDepthOne.querySelectorAll(
    `${selectors.navDepthOne} > li:not(${selectors.itemParent}) > ${selectors.navLink}`
  );
  const logoTrigger = node.querySelector(selectors.logoTrigger);
  const quickSearchTrigger = node.querySelector(selectors.quickSearchTrigger);
  const accountTrigger = node.querySelector(
    `${selectors.headerAcccountIcon} > a`
  );
  const navMenuTrigger = node.querySelectorAll(
    `${selectors.navTrigger} > ${selectors.linkParent}`
  );
  const { navigationInteraction } = node.dataset;
  const mouseInteractionIsHover = navigationInteraction === "mouseover";

  const megaNavigationAnimation = animateMegaNavigation(header);

  const events = [];

  const _addEvent = (element, action, func) => {
    element.addEventListener(action, func, true);

    events.push({
      element,
      action,
      func,
    });
  };

  const _bindEvents = () => {
    _addEvent(document, "click", _handleBodyClick);

    navMenuTrigger.forEach(trigger => {
      _addEvent(trigger, "focusin", _handleFocusIn);
      _addEvent(trigger, navigationInteraction, _toggleMenu);
      if (!mouseInteractionIsHover) {
        _addEvent(trigger, "click", _preventHandler);
      }
    });

    headerLinks.forEach(link => {
      _addEvent(link, "focus", _hideOverlay);
      _addEvent(link, "focus", closeAll);
    });

    if (logoTrigger) {
      _addEvent(logoTrigger, "focus", closeAll);
      _addEvent(logoTrigger, "focus", _hideOverlay);
    }

    if (quickSearchTrigger) {
      _addEvent(quickSearchTrigger, "focus", closeAll);
      _addEvent(quickSearchTrigger, "focus", _hideOverlay);
    }

    if (accountTrigger) {
      _addEvent(accountTrigger, "focus", closeAll);
      _addEvent(accountTrigger, "focus", _hideOverlay);
    }

    // Init scroll watcher
    _addEvent(navDepthOne, "scroll", _handleScroll);
  };

  // Close all nav drop downs if anything is clicked outside of menu
  const _handleBodyClick = event => {
    if (!getState().headerOverlayOpen) return;
    if (
      event.target.matches(
        "[data-navigation-dropdown-trigger], [data-navigation-dropdown-trigger] *"
      )
    ) {
      return;
    }

    if (
      !event.target.matches(
        "[data-navigation-dropdown], [data-navigation-dropdown] *"
      )
    ) {
      _hideOverlay();
      closeAll();
    }
  };

  const _handleFocusIn = event => {
    event.preventDefault();
    if (document.body.classList.contains("user-is-tabbing")) {
      _toggleMenu(event);
    }
  };

  const _handleMouseleave = event => {
    if (isInLiminalSpace(event)) return;

    closeAll();
    _hideOverlay();
  };

  /**
   * Check if the pointer is in the space between the trigger element and the submenu
   * @param {MouseEvent} event
   * @returns {boolean}
   */
  function isInLiminalSpace({ target, clientX: pointerX, clientY: pointerY }) {
    const childList = target.querySelector('ul');
    if (!childList) return false

    const { x: xMin, right: xMax, bottom: yMin } = target.getBoundingClientRect();
    const { top: yMax } = childList.getBoundingClientRect();

    return pointerX >= xMin
        && pointerX <= xMax
        && pointerY >= yMin
        && pointerY <= yMax
  }

  const _toggleMenu = event => {
    event.preventDefault();
    const element = event.currentTarget.parentNode;
    const menu = element.querySelector(selectors.subMenu);

    if (menu.classList.contains("visible") && !mouseInteractionIsHover) {
      _closeMenu(element);
    } else {
      emit("headerOverlay:show");
      _openMenu(element);
    }

    if (mouseInteractionIsHover && !menu.classList.contains("nav--depth-3")) {
      menu.addEventListener("mouseleave", _handleMouseleave, { once: true });
      element.addEventListener("mouseleave", _handleMouseleave, { once: true });
    }
  };

  const _preventHandler = e => {
    e.preventDefault();
  };

  const _hideOverlay = () => {
    emit("headerOverlay:hide");
  };

  const _openMenu = target => {
    if (!target.parentNode.classList.contains("nav--depth-2")) {
      closeAll();
    } else {
      // Close all submenus
      target.parentNode
        .querySelectorAll(selectors.navTrigger)
        .forEach(submenu => _closeMenu(submenu));
    }

    const menu = target.querySelector(selectors.subMenu);
    const menuLink = target.querySelector(selectors.navLink);

    menu.classList.add("visible");
    menuLink.classList.add(selectors.activeMenu);
    menuLink.setAttribute("aria-expanded", true);

    megaNavigationAnimation?.open(menu);
  };

  const _closeMenu = target => {
    const menu = target.querySelector(selectors.subMenu);
    const menuLink = target.querySelector(selectors.navLink);

    if (!target.parentNode.classList.contains("nav--depth-2")) {
      _hideOverlay();
    }

    menu.classList.remove("visible");
    menuLink.classList.remove(selectors.activeMenu);
    menuLink.setAttribute("aria-expanded", false);

    setTimeout(() => {
      megaNavigationAnimation?.close(menu);
    }, 300);
  };

  const closeAll = () => {
    node.classList.remove();
    const menuTriggers = node.querySelectorAll(selectors.navTrigger);
    menuTriggers.forEach(trigger => {
      const menu = trigger.querySelector(selectors.subMenu);
      const menuLink = trigger.querySelector(selectors.navLink);

      menu.classList.remove("visible");
      menuLink.classList.remove(selectors.activeMenu);
      menuLink.setAttribute("aria-expanded", false);

      setTimeout(() => {
        megaNavigationAnimation?.close(menu);
      }, 300);
    });
  };

  const _handleScroll = () => {
    var scroll = navDepthOne.scrollLeft;

    const root = document.documentElement;
    root.style.setProperty("--navigation-scroll-offset", `${scroll}px`);
  };

  // https://stackoverflow.com/a/9333474
  function absleft(el) {
    var x = 0;
    for (; el; el = el.offsetParent) {
      x += el.offsetLeft;
    }
    return x;
  }

  function overflows(el, opt_container) {
    const left = absleft(el);
    const right = left + el.offsetWidth;
    const cleft = absleft(opt_container);
    const cright = cleft + opt_container.offsetWidth;
    return left < cleft || right > cright;
  }

  // Depth 3 menus can overflow the document need to check and adjust
  const _handleSubmenuPageOverflow = menus => {
    menus.forEach(menu => {
      if (overflows(menu, document.body)) {
        menu.classList.add("overflows");
      }
    });
  };

  _bindEvents();
  _handleSubmenuPageOverflow(navDepthThree);

  const unload = () => {
    events.forEach(event => {
      event.element.removeEventListener(event.action, event.function);
    });

    if (resizeWatcher) {
      resizeWatcher.destroy();
    }
  };

  return { unload, closeAll };
};

export default navigation;
